// Orenza Input — text/email/password variants. Hairline Mist border, Cobalt focus ring.

import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  rightSlot?: ReactNode;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, rightSlot, containerClassName, className, id, ...rest },
  ref,
) {
  // Always associate the label — most call sites pass neither id nor name,
  // which left labels programmatically unattached (screen readers + tests).
  const autoId = useId();
  const inputId = id ?? rest.name ?? autoId;
  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      {label && (
        <label htmlFor={inputId} className="text-micro text-cool font-medium uppercase">
          {label}
        </label>
      )}
      <div
        className={cn(
          // One focus indicator, not two. The border stays the SAME width and colour
          // on focus (so it doesn't read as a hard inner line); a single soft cobalt
          // ring is the whole emphasis. Stacking a cobalt border AND a cobalt ring is
          // what produced the "double indigo outline" on every input.
          'flex items-center gap-2 h-10 px-3 bg-frost border border-mist rounded-md transition-[border,box-shadow]',
          'focus-within:border-cobalt/60 focus-within:shadow-focus',
          error && 'border-crimson focus-within:border-crimson/60 focus-within:shadow-[0_0_0_3px_rgba(225,29,72,0.22)]',
        )}
      >
        <input
          ref={ref}
          id={inputId}
          className={cn(
            'flex-1 bg-transparent outline-none text-body-md text-obsidian placeholder:text-cool',
            className,
          )}
          {...rest}
        />
        {rightSlot}
      </div>
      {hint && !error && <span className="text-caption text-slate">{hint}</span>}
      {error && <span className="text-caption text-crimson">{error}</span>}
    </div>
  );
});
